// Home sliders further down the page (prompt 122, sections/ssc-product-row.liquid → "Load when near the screen").
// The page prints each row's head and an empty track; this fills the track through the Section Rendering API when it is
// ~1.5 screens from the visible area (below or above). The empty track is held at the height of a loaded row (track + dots, measured
// from the first full product row on the page) so nothing below moves when the cards arrive.
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

const AHEAD = 1.5; // screens
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const waiting = () => [...document.querySelectorAll('[data-ssc-row-lazy]')];

// Height of a loaded row from the top of its track to the bottom of its dots.
function measure() {
  // A filled product slider (not the lineup or recently viewed rows, which share the track class).
  const ref = [...document.querySelectorAll('.ssc-row-section [data-ssc-row] [data-ssc-row-track]:not([data-ssc-row-lazy])')].find((t) => t.children.length);
  if (!ref) return;
  const top = ref.getBoundingClientRect().top;
  const dots = ref.nextElementSibling && ref.nextElementSibling.classList.contains('ssc-dots') && !ref.nextElementSibling.hidden ? ref.nextElementSibling : null;
  const bottom = dots ? dots.getBoundingClientRect().bottom : ref.getBoundingClientRect().bottom;
  if (bottom - top > 0) document.documentElement.style.setProperty('--ssc-row-wait', `${Math.round(bottom - top)}px`);
}

function arrows(row, track) {
  // One card per click (prompt 124), like the rows above (assets/ssc-global.js).
  const by = (direction) => {
    const item = track.querySelector('.ssc-row__item');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * (item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8), behavior: reduce ? 'auto' : 'smooth' });
  };
  row.querySelector('[data-ssc-row-prev]')?.addEventListener('click', () => by(-1));
  row.querySelector('[data-ssc-row-next]')?.addEventListener('click', () => by(1));
}

async function fill(track) {
  if (track.dataset.sscRowState) return;
  track.dataset.sscRowState = 'loading';
  const id = track.dataset.sscRowLazy;
  const row = track.closest('[data-ssc-row]');
  try {
    const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(id)}`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const loaded = doc.querySelector('[data-ssc-row-track]');
    if (!loaded || !loaded.children.length) {
      // Nothing to show (e.g. no product passed "Most sizes in stock only"): the row goes, like a row with 0 products.
      track.closest('.shopify-section')?.setAttribute('hidden', '');
      return;
    }
    // Keep the held height until the dots are drawn, then let the row size itself.
    track.replaceChildren(...loaded.children);
    track.removeAttribute('aria-busy');
    track.dataset.sscRowState = 'done';
    if (window.SSCRow) window.SSCRow.init(row);
    arrows(row, track);
    requestAnimationFrame(() => track.removeAttribute('data-ssc-row-lazy'));
  } catch (error) {
    delete track.dataset.sscRowState;
  }
}

let observer = null;
function watch() {
  if (observer) observer.disconnect();
  const tracks = waiting().filter((t) => !t.dataset.sscRowState);
  if (!tracks.length) return;
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        fill(entry.target);
      });
    },
    // Both ways: a row above the screen (Back to the bottom of the page, then scrolling up) fills before it shows too.
    { root: getIntersectionRoot(), rootMargin: `${AHEAD * 100}% 0px ${AHEAD * 100}% 0px` }
  );
  tracks.forEach((t) => observer.observe(t));
}

measure();
// assets/ssc-row.js draws the dots on DOMContentLoaded (its listener runs first): measure again with them, and after load.
document.addEventListener('DOMContentLoaded', measure, { once: true });
window.addEventListener('load', measure, { once: true });
watch();
scrollContainerMediaQuery.addEventListener('change', () => {
  measure();
  watch();
});
